// AdminPromoCodes.tsx
import React, { useCallback, useEffect, useState } from 'react';
import {
    adminFetchPromoCodes,
    adminCreatePromoCode,
    adminUpdatePromoCode,
    adminFetchPromoCodeRedemptions,
    adminDeletePromoCode,
    type PromoCode,
    type PromoRedemption,
} from '../../../../api/promoCodes';
import '../../../../assets/styles/promo-codes.scss';

function randomCode(len = 8) {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let out = '';
    for (let i = 0; i < len; i++)
        out += alphabet[Math.floor(Math.random() * alphabet.length)];
    return out;
}

const PLAN_PROFILES: Record<'BASIC' | 'STANDARD' | 'FAMILY', 1 | 2 | 4> = {
    BASIC: 1,
    STANDARD: 2,
    FAMILY: 4,
};

type PlanType = 'BASIC' | 'STANDARD' | 'FAMILY';

export default function PromoCodes() {
    /* ---------- Kody rabatowe ---------- */
    const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
    const [newCode, setNewCode] = useState<{
        code: string;
        description: string;
        months: number;
        plan_type: PlanType;
        max_redemptions: number | null;
        starts_at: string; // YYYY-MM-DD
        expires_at: string; // YYYY-MM-DD
    }>({
        code: '',
        description: '',
        months: 1,
        plan_type: 'BASIC',
        max_redemptions: 1,
        starts_at: '',
        expires_at: '',
    });

    const [selectedCode, setSelectedCode] = useState<PromoCode | null>(null);
    const [redemptions, setRedemptions] = useState<PromoRedemption[]>([]);

    /* ---------- Batch kodów ---------- */
    const [batchQty, setBatchQty] = useState(5);
    const [batchPrefix, setBatchPrefix] = useState('PROMO');
    const [batchDesc, setBatchDesc] = useState('Kod promocyjny – grant premium');
    const [batchMonths, setBatchMonths] = useState(1);
    const [batchPlan, setBatchPlan] = useState<PlanType>('BASIC');

    const loadCodes = useCallback(async () => {
        try {
            const res = await adminFetchPromoCodes();
            setPromoCodes(res.items);
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        loadCodes();
    }, [loadCodes]);

    async function createBatchSingleUseCodes() {
        if (batchQty < 1 || batchQty > 100) {
            alert('Podaj liczbę kodów 1–100');
            return;
        }

        const months = Math.max(1, Number(batchMonths || 1));

        const toCreate = Array.from({ length: batchQty }, (_, i) => {
            const code = `${batchPrefix}${i === 0 ? '' : '-'}${randomCode(8)}`;
            return { code, description: batchDesc };
        });

        const createdList: PromoCode[] = [];
        for (const item of toCreate) {
            const created = await adminCreatePromoCode({
                code: item.code,
                description: item.description,
                benefit_type: 'PREMIUM_MONTH',
                max_redemptions: 1,
                is_active: true,
                months,
                plan_type: batchPlan,
            } as any);
            createdList.push(created);
        }
        setPromoCodes(prev => [...createdList, ...prev]);
        alert(`Utworzono ${createdList.length} kodów jednorazowych.`);
    }

    function formatGrant(pc: PromoCode) {
        const m = pc.months ?? 1;
        const plan = (pc.plan_type as PlanType) || 'BASIC';
        const profiles = PLAN_PROFILES[plan] ?? 1;
        return `${m} m-c (${plan}, profili: ${profiles})`;
    }

    return (
        <div className="admin-root users-violet">
            <section className="grid-2">
                {/* LEWA KOLUMNA – lista kodów + pojedynczy kod */}
                <div className="card">
                    <h2>Kody rabatowe (grant premium)</h2>
                    <p className="muted">
                        Każdy kod nadaje użytkownikowi premium na określoną liczbę miesięcy.
                    </p>

                    {/* Formularz nowego kodu */}
                    <div className="form-column" style={{ gap: 8, marginBottom: 16 }}>
                        <div className="form-row codes-row">
                            <input
                                className="input promo-code-title"
                                value={newCode.code}
                                onChange={e =>
                                    setNewCode(s => ({
                                        ...s,
                                        code: e.target.value.toUpperCase(),
                                    }))
                                }
                                placeholder="KOD123"
                                aria-label="Kod"
                            />
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() =>
                                    setNewCode(s => ({
                                        ...s,
                                        code: randomCode(8),
                                    }))
                                }
                                title="Wygeneruj losowy kod"
                            >
                                Losowy
                            </button>
                        </div>

                        <div className="form-row codes-row">
                            <input
                                className="input promo-code-title"
                                value={newCode.description}
                                onChange={e =>
                                    setNewCode(s => ({ ...s, description: e.target.value }))
                                }
                                placeholder="Opis (np. '1 m-c BASIC dla kampanii X')"
                                aria-label="Opis"
                            />
                        </div>

                        <div className="form-row codes-row">
                            <div className="field">
                                <label>Miesiące (grant premium)</label>
                                <input
                                    type="number"
                                    min={1}
                                    className="input short"
                                    value={newCode.months}
                                    onChange={e =>
                                        setNewCode(s => ({
                                            ...s,
                                            months: Math.max(
                                                1,
                                                parseInt(e.target.value || '1', 10)
                                            ),
                                        }))
                                    }
                                />
                            </div>

                            <div className="field">
                                <label>Plan</label>
                                <select
                                    className="select"
                                    value={newCode.plan_type}
                                    onChange={e => {
                                        const p = e.target.value as PlanType;
                                        setNewCode(s => ({ ...s, plan_type: p }));
                                    }}
                                >
                                    <option value="BASIC">BASIC (1 profil)</option>
                                    <option value="STANDARD">STANDARD (2 profile)</option>
                                    <option value="FAMILY">FAMILY (4 profile)</option>
                                </select>
                            </div>

                            <div className="field">
                                <label>Max użyć</label>
                                <input
                                    type="number"
                                    min={1}
                                    className="input short"
                                    value={newCode.max_redemptions ?? 1}
                                    onChange={e =>
                                        setNewCode(s => ({
                                            ...s,
                                            max_redemptions: Math.max(
                                                1,
                                                parseInt(e.target.value || '1', 10)
                                            ),
                                        }))
                                    }
                                />
                            </div>
                        </div>

                        <div className="form-row codes-row">
                            <div className="field">
                                <label>Aktywny od (opcjonalnie)</label>
                                <input
                                    type="date"
                                    className="input"
                                    value={newCode.starts_at}
                                    onChange={e =>
                                        setNewCode(s => ({
                                            ...s,
                                            starts_at: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div className="field">
                                <label>Aktywny do (opcjonalnie)</label>
                                <input
                                    type="date"
                                    className="input"
                                    value={newCode.expires_at}
                                    onChange={e =>
                                        setNewCode(s => ({
                                            ...s,
                                            expires_at: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>

                        <div className="form-row codes-row">
                            <button
                                className="btn btn-primary"
                                onClick={async () => {
                                    if (!newCode.code.trim()) {
                                        alert('Podaj kod');
                                        return;
                                    }
                                    const months = Math.max(1, Number(newCode.months || 1));

                                    const payload: Partial<PromoCode> = {
                                        code: newCode.code.trim(),
                                        description:
                                            newCode.description?.trim() || undefined,
                                        benefit_type: 'PREMIUM_MONTH',
                                        months,
                                        plan_type: newCode.plan_type,
                                        max_redemptions: newCode.max_redemptions ?? 1,
                                        is_active: true,
                                    };

                                    // daty – jeśli podane, konwertujemy na ISO
                                    if (newCode.starts_at) {
                                        payload.starts_at = new Date(
                                            `${newCode.starts_at}T00:00:00`
                                        ).toISOString();
                                    }
                                    if (newCode.expires_at) {
                                        payload.expires_at = new Date(
                                            `${newCode.expires_at}T23:59:59`
                                        ).toISOString();
                                    }

                                    const created = await adminCreatePromoCode(
                                        payload as any
                                    );
                                    setNewCode({
                                        code: '',
                                        description: '',
                                        months: 1,
                                        plan_type: 'BASIC',
                                        max_redemptions: 1,
                                        starts_at: '',
                                        expires_at: '',
                                    });
                                    setPromoCodes(prev => [created, ...prev]);
                                }}
                            >
                                Dodaj kod
                            </button>

                            <button
                                onClick={loadCodes}
                                className="btn btn-ghost"
                                title="Odśwież"
                                type="button"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <polyline points="23 4 23 10 17 10"></polyline>
                                    <polyline points="1 20 1 14 7 14"></polyline>
                                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
                                    <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Lista istniejących kodów */}
                    <div className="list">
                        {promoCodes.map(pc => (
                            <div key={pc.id} className="list-item">
                                <div className="list-meta">
                                    <div className="mono strong">{pc.code}</div>
                                    <span className="muted small">
                                        {' '}
                                        — {pc.description || 'brak opisu'} • Grant:{' '}
                                        {formatGrant(pc)}
                                        {pc.max_redemptions ? (
                                            <> • max użyć: {pc.max_redemptions}</>
                                        ) : null}
                                        {pc.starts_at ? (
                                            <>
                                                {' '}
                                                • od:{' '}
                                                {new Date(pc.starts_at).toLocaleDateString()}
                                            </>
                                        ) : null}
                                        {pc.expires_at ? (
                                            <>
                                                {' '}
                                                • do:{' '}
                                                {new Date(pc.expires_at).toLocaleDateString()}
                                            </>
                                        ) : null}
                                    </span>
                                </div>
                                <div className="list-actions">
                                    <span
                                        className={`dot ${pc.is_active ? 'dot-on' : 'dot-off'
                                            }`}
                                        title={pc.is_active ? 'aktywne' : 'wyłączone'}
                                    />
                                    <button
                                        className="btn btn-ghost"
                                        onClick={async () => {
                                            setSelectedCode(pc);
                                            const r = await adminFetchPromoCodeRedemptions(
                                                pc.id
                                            );
                                            setRedemptions(r);
                                        }}
                                    >
                                        Użycia
                                    </button>
                                    <button
                                        className="btn btn-ghost"
                                        onClick={async () => {
                                            const upd = await adminUpdatePromoCode(pc.id, {
                                                is_active: !pc.is_active,
                                            });
                                            setPromoCodes(prev =>
                                                prev.map(x => (x.id === pc.id ? upd : x))
                                            );
                                        }}
                                    >
                                        {pc.is_active ? 'Wyłącz' : 'Włącz'}
                                    </button>
                                    <button
                                        className="btn btn-danger"
                                        onClick={async () => {
                                            if (
                                                !window.confirm(
                                                    `Usunąć kod ${pc.code}?`
                                                )
                                            )
                                                return;
                                            await adminDeletePromoCode(pc.id);
                                            setPromoCodes(prev =>
                                                prev.filter(x => x.id !== pc.id)
                                            );
                                            if (selectedCode?.id === pc.id) {
                                                setSelectedCode(null);
                                                setRedemptions([]);
                                            }
                                        }}
                                        title="Usuń kod"
                                    >
                                        <svg
                                            viewBox="0 0 24 24"
                                            width="16"
                                            height="16"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path d="M3 6h18"></path>
                                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                        {promoCodes.length === 0 && (
                            <div className="empty">
                                Brak kodów. Dodaj pierwszy kod powyżej.
                            </div>
                        )}
                    </div>
                </div>

                {/* PRAWA KOLUMNA – batch + użycia kodów */}
                <div className="card">
                    <h2>Batch – jednorazowe kody</h2>
                    <p className="muted">
                        Wygeneruj kilka kodów, z limitem użyć = 1. Każdy kod nadaje
                        premium na X miesięcy zgodnie z ustawieniem poniżej.
                    </p>

                    <div className="form-row codes-row">
                        <div className="field">
                            <label htmlFor="batchQty">Ilość</label>
                            <input
                                id="batchQty"
                                type="number"
                                min={1}
                                max={100}
                                className="input short"
                                value={batchQty}
                                onChange={e =>
                                    setBatchQty(
                                        parseInt(e.target.value || '0', 10) || 0
                                    )
                                }
                            />
                        </div>

                        <div className="field">
                            <label htmlFor="batchPrefix">Prefiks</label>
                            <input
                                id="batchPrefix"
                                className="input"
                                value={batchPrefix}
                                onChange={e =>
                                    setBatchPrefix(e.target.value.toUpperCase())
                                }
                            />
                        </div>

                        <div className="field">
                            <label htmlFor="batchDesc">Opis</label>
                            <input
                                id="batchDesc"
                                className="input"
                                value={batchDesc}
                                onChange={e => setBatchDesc(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="form-row codes-row">
                        <div className="field">
                            <label>Miesiące (grant)</label>
                            <input
                                type="number"
                                min={1}
                                className="input short"
                                value={batchMonths}
                                onChange={e =>
                                    setBatchMonths(
                                        Math.max(
                                            1,
                                            parseInt(e.target.value || '1', 10)
                                        )
                                    )
                                }
                            />
                        </div>

                        <div className="field">
                            <label>Plan</label>
                            <select
                                className="select"
                                value={batchPlan}
                                onChange={e => {
                                    const p = e.target.value as PlanType;
                                    setBatchPlan(p);
                                }}
                            >
                                <option value="BASIC">BASIC (1)</option>
                                <option value="STANDARD">STANDARD (2)</option>
                                <option value="FAMILY">FAMILY (4)</option>
                            </select>
                        </div>

                        <button
                            className="btn btn-primary"
                            onClick={createBatchSingleUseCodes}
                        >
                            Generuj kody
                        </button>
                    </div>

                    <div className="table-wrapper">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Użytkownik</th>
                                    <th>Data użycia</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!selectedCode && (
                                    <tr>
                                        <td colSpan={2} className="empty">
                                            Wybierz kod z listy po lewej, by zobaczyć użycia.
                                        </td>
                                    </tr>
                                )}
                                {selectedCode && redemptions.length === 0 && (
                                    <tr>
                                        <td colSpan={2} className="empty">
                                            Brak użyć dla tego kodu
                                        </td>
                                    </tr>
                                )}
                                {selectedCode &&
                                    redemptions.map(r => (
                                        <tr key={r.id}>
                                            <td>{r.email}</td>
                                            <td>
                                                {new Date(
                                                    r.redeemed_at
                                                ).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
}
